import * as amplitude from "@amplitude/analytics-react-native"
import { IS_WEB } from "@common/utils/mobileUtils"
import Constants from "expo-constants"
import * as Linking from "expo-linking"
import { Platform } from "react-native"

// Où pointer l'utilisateur pour installer l'appli, et comment mesurer le clic.
// La résolution d'URL s'était recopiée d'écran en écran et les copies avaient
// commencé à diverger sur les valeurs de repli.
//
// Deux appelants sont migrés ici : le bandeau web mobile du live et le bouton
// de téléchargement. Les autres écrans qui résolvent encore l'URL chez eux
// migreront au fil de l'eau — leur reprise fait l'objet d'un suivi dédié.

// Dernier filet si ni le back ni la config Expo ne fournissent l'URL
const FALLBACK_URLS = {
    ios: "https://apps.apple.com/app/masteur/id1614253966",
    android: "https://play.google.com/store/apps/details?id=com.masteur.app",
}

/**
 * "android" si l'appareil tourne sous Android, "ios" sinon. En natif on lit la
 * plateforme : l'user agent n'existe pas, et s'y fier renverrait un utilisateur
 * Android vers l'App Store.
 */
const userAgent = () => navigator?.userAgent || ""

export const detectStore = () => {
    if (!IS_WEB) return Platform.OS === "android" ? "android" : "ios"

    return /android/i.test(userAgent()) ? "android" : "ios"
}

// Un iPad sous Safari se présente comme un Mac : seul l'écran tactile le trahit.
const isAppleMobileWeb = () =>
    /iphone|ipad|ipod/i.test(userAgent()) ||
    (/macintosh/i.test(userAgent()) && navigator?.maxTouchPoints > 1)

/**
 * Les stores à proposer sur le web : celui du téléphone, les deux sur un
 * ordinateur (le parent installera sur son téléphone). Sur Android, le badge
 * App Store menait à une page Apple sans installation possible.
 */
export const webStoresToShow = () => {
    if (/android/i.test(userAgent())) return ["android"]
    if (isAppleMobileWeb()) return ["ios"]
    return ["ios", "android"]
}

/**
 * Lien du badge Google Play sur le web. Dans un navigateur Android, la fiche
 * ouverte comme une page web reste dans le navigateur, où « Installer » exige
 * un compte Google connecté : un lien `intent://` la confie à l'application
 * Play Store, et retombe sur la fiche web quand elle n'est pas là.
 *
 * Pas dans une WebView (navigateurs intégrés d'Instagram, de Facebook…) : elle
 * n'ouvre un `intent://` que si l'application hôte le prend, sinon elle affiche
 * une erreur sans passer par la fiche de repli.
 */
export const storeHref = (store, url) => {
    const ua = userAgent()
    if (store !== "android" || !IS_WEB || !url) return url
    if (!/android/i.test(ua) || /; wv\)/.test(ua)) return url
    const packageId = url.match(/[?&]id=([^&#]+)/)?.[1]
    if (!packageId) return url
    return (
        `intent://details?id=${packageId}#Intent;scheme=market;` +
        `package=com.android.vending;` +
        `S.browser_fallback_url=${encodeURIComponent(url)};end`
    )
}

/**
 * URL de la fiche store, par ordre de préférence : valeur pilotée par le back
 * (staticData), puis config Expo, puis valeur de repli.
 */
export const resolveStoreUrl = (store, { iosAppUrl, androidAppUrl } = {}) =>
    store === "android"
        ? androidAppUrl?.trim() ||
          Constants?.expoConfig?.android?.playStoreUrl ||
          FALLBACK_URLS.android
        : iosAppUrl?.trim() ||
          Constants?.expoConfig?.ios?.appStoreUrl ||
          FALLBACK_URLS.ios

/** Mesure le clic ; `source` identifie l'écran d'où il part. */
export const trackStoreClick = (store, source) => {
    if (!source) return
    amplitude.track("download_app.store_click", { store, source })
}

/**
 * Ouvre la fiche store. Sur le web, dans un nouvel onglet : l'utilisateur peut
 * être en pleine séance, on ne quitte pas la page.
 */
export const openStoreUrl = (url) => {
    if (!url) return
    if (IS_WEB) {
        window.open(url, "_blank", "noopener,noreferrer")
    } else {
        Linking.openURL(url).catch(() => {})
    }
}
